// Portfolio Administration v1.0 PR 3 — TypeScript shapes mirroring the
// PR 2a/2b/2c backend DTOs in
// src/PhoenixCapital.Core/DTOs/PortfolioAdmin/PortfolioAdminDtos.cs.
// Field names match the JSON wire (camelCase) emitted by the
// PortfolioAdmin controllers.

// ============================== loan summary ==========================

export interface LoanSummaryDto {
  id: string;
  loanNumber: string;
  accountState: string | null;
  setupComplete: boolean;
  setupCompletedDate: string | null;
  currentBalance: number;
  /** Originating Deal (spec §7.1) — header renders the back-link. */
  dealId: string;
  dealNumber: string | null;
  /** Resolved server-side (LegalEntity → Person fallback). */
  borrowerName: string | null;
  paymentDay: number;
  /** 1 = Monthly (the only value in v1). */
  paymentFrequency: number;
  /** Derived from the v1 loan tape (open, unpaid, past-due entries). */
  daysDelinquent: number;
  firstPaymentDate: string;
  paymentPeriodsRecorded: number;
  covenantThresholdsJson: string | null;
  latestDscr: number | null;
  latestDscrStatus: string | null;
  latestDscrPeriodEnd: string | null;
  /** Executed terms set at closing handoff from the Final Financial
   *  Addendum. interestRate is a decimal FRACTION (0.075 = 7.5%). */
  originalLoanAmount: number;
  interestRate: number;
  termMonths: number;
  amortizationMonths: number;
  maturityDate: string;
  /** Profile tab editing (B6) — set once the terms have been corrected
   *  post-close ("Last edited by {name}, {date}" caption). */
  termsEditedAt: string | null;
  termsEditedByName: string | null;
  /** Portfolios — current grouping (name server-resolved for the header). */
  portfolioId: string | null;
  portfolioName: string | null;
  /** WS8 — the loan's assigned TPS provider (the WF1 platform fee + NSF
   *  lines route to this firm's primary operating account). Backfilled
   *  to the seeded default for every existing loan at WS-0 migration time;
   *  closing default-assigns it to new loans. Optional on the type so
   *  pre-WS8 inline test fixtures keep compiling — wire payload always
   *  carries the field. */
  tpsId?: string | null;
  /** Covenant Calibration (Profile tab) — true once all three capture
   *  cards (calibration amounts, seasonality, DSCR/DACA banking) are
   *  confirmed. Drives the Profile tab's "!" warning badge until then.
   *  Optional so inline test fixtures keep compiling (mirrors `tpsId`);
   *  the wire payload always carries the field. */
  covenantCalibrationConfirmed?: boolean;
  /** Pre-pool waterfall alignment — the note's fee terms. Percentages are
   *  decimal FRACTIONS (0.05 = 5%); null = the note has no such clause and
   *  the automatic assessment engines stay off. Optional so inline test
   *  fixtures keep compiling (mirrors `tpsId`); the wire payload always
   *  carries the fields. */
  lateFeePct?: number | null;
  lateFeeGraceDays?: number | null;
  nsfFeeAmount?: number | null;
  defaultInterestRatePct?: number | null;
  prepaymentFeePct?: number | null;
  /** Payment due-date business-day rule (Core Agreement §2.4). true = period 1
   *  on the closing date, period 2+ on the first business day of each calendar
   *  month; false/undefined = grandfathered legacy fixed day-of-month. Optional
   *  so inline test fixtures keep compiling (mirrors `tpsId`); the wire payload
   *  always carries the field. Drives the "Payment day" display copy. */
  usesBusinessDayScheduleRule?: boolean;
  /** Closing-gate BYPASS — set when this loan was boarded off a TEST deal whose
   *  Escrow Setup gate was force-completed by an ADMIN (no verified funds).
   *  Drives the header's "Bypassed test loan" badge. Null on a normal loan.
   *  Optional so inline fixtures keep compiling; the wire payload always
   *  carries the field. */
  closingBypassNote?: string | null;
  /** Buyer Consulting Fee — Phoenix's carve, monthly leg (WF1 P10), as a
   *  FRACTION (0.02 = 2%). Carved out of the SELLER's distribution; the
   *  buyer's payment amount does not change.
   *
   *  0 is the OFF state and a REAL answer ("not charged on this loan"), never
   *  a missing one — the column is NOT NULL, unlike the nullable note fee
   *  terms above where a nil legitimately means "the note has no such clause".
   *  Render it as "0%", never as an em-dash.
   *
   *  Optional here only so inline fixtures keep compiling (the LoanSummaryDto
   *  convention); the wire payload always carries the field. Read as `?? 0`. */
  buyerConsultingFeeMonthlyPct?: number;
  /** The liquidity leg (WF1 P11) — a FRACTION of the gross remitted at a
   *  payoff, balloon, refinance or full prepayment. Same OFF-state semantics
   *  as the monthly leg; the two are INDEPENDENT and either may be zero while
   *  the other is not. */
  buyerConsultingFeeLiquidityPct?: number;
  /** Payment Administration Fee (BLA Core §2.4) — a FRACTION of each scheduled
   *  monthly payment of principal and interest (a fraction, not a percent), charged to the
   *  BORROWER on top and debited with the payment as one combined amount.
   *
   *  🔴 THREE STATES, NOT TWO, AND THE DIFFERENCE IS THE POINT.
   *    `null`      the note carries NO such term — every loan boarded before
   *                the column existed, never backfilled, with no fallback to
   *                the standard rate. Render it as an em-dash, NOT as "0%".
   *    `0`         the term exists and bills nothing.
   *    `> 0`       the rate.
   *
   *  That is deliberately UNLIKE the two Buyer Consulting rates directly above,
   *  which are NOT NULL with a 0.00 off state — so `?? 0` is the right read for
   *  those and the WRONG read for this one, because it collapses the first
   *  state onto the second. The entity comment warns that harmonizing the
   *  columns is a regression rather than a cleanup.
   *
   *  `undefined` here means "an older payload / an inline fixture omitted it"
   *  and is treated exactly as `null`: unknown and no-such-term both render the
   *  same em-dash, and neither may render a rate. */
  paymentAdministrationFeePct?: number | null;
}

/** Profile tab editing (B6) — loan-terms correction. interestRate is a
 *  decimal FRACTION (0.075 = 7.5%); reason is required (executed contract
 *  terms — audited with before/after). */
export interface UpdateLoanTermsRequest {
  originalLoanAmount: number;
  interestRate: number;
  termMonths: number;
  amortizationMonths: number;
  maturityDate: string;
  firstPaymentDate: string;
  paymentDay: number;
  paymentFrequency: number;
  /** Note fee terms — the form always sends the full set (FIND-12), so
   *  null CLEARS the term (no such clause; automatic assessment off).
   *  Percentages are decimal FRACTIONS (0.05 = 5%). */
  lateFeePct: number | null;
  lateFeeGraceDays: number | null;
  nsfFeeAmount: number | null;
  defaultInterestRatePct: number | null;
  prepaymentFeePct: number | null;
  reason: string;
}

/** Profile tab editing (B6) — add ("Add period") or edit one
 *  financial-history row. */
export interface UpsertLoanFinancialPeriodRequest {
  year: number;
  periodType: "Annual" | "TTM";
  revenue: number | null;
  ebitda: number | null;
}

// ============================== covenant calibration ==================
// Capture-only — three data-entry cards on the loan Profile tab. NO
// calculations, NO derived values, NO automation. Field names mirror the
// backend CovenantCalibration DTOs (camelCase wire).

export type CovenantDesignation = "Standard" | "Seasonal" | "ProjectBased";

export type CovenantMeasurementWindow =
  | "Quarterly"
  | "SemiAnnual"
  | "Annual"
  | "TwentyFourMonth"
  | "Custom";

export type DscrBankAccountType = "Operating" | "Reserve" | "Other";

export type DscrBankAccountPurpose =
  | "DscrMonitoring"
  | "Reserve"
  | "Payment"
  | "Other";

export const COVENANT_DESIGNATION_LABEL: Record<CovenantDesignation, string> = {
  Standard: "Standard",
  Seasonal: "Seasonal",
  ProjectBased: "Project-Based",
};

export const COVENANT_MEASUREMENT_WINDOW_LABEL: Record<
  CovenantMeasurementWindow,
  string
> = {
  Quarterly: "Quarterly",
  SemiAnnual: "Semi-annual",
  Annual: "Annual",
  TwentyFourMonth: "24-month",
  Custom: "Custom",
};

export const DSCR_BANK_ACCOUNT_TYPE_LABEL: Record<DscrBankAccountType, string> = {
  Operating: "Operating",
  Reserve: "Reserve",
  Other: "Other",
};

export const DSCR_BANK_ACCOUNT_PURPOSE_LABEL: Record<
  DscrBankAccountPurpose,
  string
> = {
  DscrMonitoring: "DSCR monitoring",
  Reserve: "Reserve",
  Payment: "Payment",
  Other: "Other",
};

/** One calibration row — a labelled threshold pre-filled with a starting
 *  money value. Staff edit + confirm; `code` is the stable server key. */
export interface CovenantCalibrationAmountDto {
  code: string;
  label: string;
  section: string;
  amount: number;
}

export interface CovenantSeasonalityDto {
  designation: CovenantDesignation;
  measurementWindow: CovenantMeasurementWindow | null;
  reserveMonths: number | null;
  notes: string | null;
}

export interface CovenantBankAccountDto {
  id: string;
  institutionName: string;
  accountType: DscrBankAccountType;
  /** Server returns a masked last-4 ("••••1234"); full numbers never
   *  leave the server. */
  accountNumberMasked: string | null;
  purpose: DscrBankAccountPurpose;
  dacaExecuted: boolean;
  dacaDate: string | null;
  isReserveAccount: boolean;
}

/** Per-card confirmation stamp (server records confirmedBy/confirmedAt). */
export interface CovenantCardConfirmationDto {
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedByName: string | null;
}

export interface CovenantCalibrationDto {
  loanId: string;
  amounts: CovenantCalibrationAmountDto[];
  seasonality: CovenantSeasonalityDto;
  accounts: CovenantBankAccountDto[];
  calibrationConfirmation: CovenantCardConfirmationDto;
  seasonalityConfirmation: CovenantCardConfirmationDto;
  bankingConfirmation: CovenantCardConfirmationDto;
}

export interface UpdateCalibrationAmountsRequest {
  amounts: { code: string; amount: number }[];
}

export interface UpdateSeasonalityRequest {
  designation: CovenantDesignation;
  measurementWindow: CovenantMeasurementWindow | null;
  reserveMonths: number | null;
  notes: string | null;
}

/** Send the full number ONLY when changed; `accountNumber: null` keeps the
 *  existing one on an update. */
export interface UpsertBankAccountRequest {
  institutionName: string;
  accountType: DscrBankAccountType;
  accountNumber: string | null;
  purpose: DscrBankAccountPurpose;
  dacaExecuted: boolean;
  dacaDate: string | null;
  isReserveAccount: boolean;
}

// Portfolio Administration v1.0 — list-page row for /portfolio-admin/loans.
// Mirrors LoanListItemDto on the backend. creditRating is the STATIC
// origination rating (A–E) set at deal close; the live health grade + real
// days late ride the batch health-grades endpoint (loanHealthApi.grades).
export interface LoanListItemDto {
  id: string;
  loanNumber: string;
  accountState: string | null;
  creditRating: string;
  originalLoanAmount: number;
  currentBalance: number;
  maturityDate: string;
  portfolioId: string | null;
  borrowerName: string | null;
  assignedB1UserId: string | null;
  assignedB1UserName: string | null;
}

/** Spec §13 — Loan AccountState values. */
export const LOAN_ACCOUNT_STATES = [
  "performing",
  "sub-performing",
  "non-performing",
  "defaulted",
  "charged-off",
  "liquidated",
  "paid-off",
] as const;
export type LoanAccountState = (typeof LOAN_ACCOUNT_STATES)[number];

/** Days-late buckets on the Loans list filter row. Applied CLIENT-SIDE over
 *  the real per-loan days from the batch health-grades payload (the old
 *  server-side AccountState proxy is gone — the buckets used to lie). */
export const LOAN_DAYS_LATE_BUCKETS = ["0", "1-29", "30-59", "60+"] as const;
export type LoanDaysLateBucket = (typeof LOAN_DAYS_LATE_BUCKETS)[number];

export function daysLateBucket(days: number): LoanDaysLateBucket {
  if (days <= 0) return "0";
  if (days <= 29) return "1-29";
  if (days <= 59) return "30-59";
  return "60+";
}

export interface LoanSearchParams {
  search?: string;
  portfolioIds?: string[];
  accountStates?: string[];
  /** Loans whose B1 (Loan Administrator) slot is assigned to this user. */
  assignedB1UserId?: string;
  /** "My loans" — any of the seven assignment pills held by the caller. */
  assignedToMe?: boolean;
}

// ============================== portfolios ============================
// Named, curated loan groupings (pre-contribution scope: grouping + audit
// trail only — no pool/conversion surface).

export interface PortfolioListItemDto {
  id: string;
  name: string;
  description: string | null;
  status: "Open" | "Archived";
  createdAt: string;
  loanCount: number;
  totalCurrentBalance: number;
}

export interface CreatePortfolioRequest {
  name: string;
  description?: string | null;
}

/** FIND-12: omit = unchanged; description "" clears. */
export interface UpdatePortfolioRequest {
  name?: string;
  description?: string;
  status?: "Open" | "Archived";
}

/** portfolioId null = remove from portfolio. A reason is required whenever
 *  any loan leaves an existing portfolio (move or remove). */
export interface MoveLoansToPortfolioRequest {
  loanIds: string[];
  portfolioId: string | null;
  reason?: string;
}

export interface MoveLoansResultDto {
  movedCount: number;
  skippedCount: number;
}

export interface PortfolioMonthlyCollectionsDto {
  year: number;
  month: number;
  total: number;
  phoenixIncome: number;
  sellerPayment: number;
}

export interface PortfolioDashboardDto {
  id: string;
  name: string;
  description: string | null;
  status: "Open" | "Archived";
  createdAt: string;
  loanCount: number;
  totalCurrentBalance: number;
  expectedMonthlyIncome: number;
  collectedYearToDate: number;
  delinquentCount: number;
  averageDscr: number | null;
  monthlyCollections: PortfolioMonthlyCollectionsDto[];
}

// ============================== setup checklist =======================

/** Spec §7.2 — single Setup catalog row state on a particular loan. */
export type LoanSetupStatus =
  | "Pending"
  | "InProgress"
  | "Complete"
  | "Certified"
  | "Declined"
  | "Waived"
  | "NA";

/** Spec §7.2 — DealKiller items must be complete; RedFlag items must be
 *  certified or waived; NeedsImprovement items can pass with any status. */
export type LoanSetupTier = "DealKiller" | "RedFlag" | "NeedsImprovement";

/** Loan Setup tier display vocabulary — DISPLAY-ONLY rename scoped to the
 *  loan Setup checklist surfaces (the stored codes and the gate semantics
 *  are unchanged, and Deal Flow's criteria vocabulary is untouched). */
export const LOAN_SETUP_TIER_LABEL: Record<LoanSetupTier, string> = {
  DealKiller: "Critical",
  RedFlag: "Important",
  NeedsImprovement: "Low",
};

/** Tolerant lookup for wire strings — unknown tiers render as-is. */
export function loanSetupTierLabel(tier: string): string {
  return LOAN_SETUP_TIER_LABEL[tier as LoanSetupTier] ?? tier;
}

export interface LoanSetupChecklistItemDto {
  id: string;
  loanId: string;
  catalogItemId: string;
  status: string;
  dueDate: string;
  assignedToUserId: string | null;
  completedDate: string | null;
  completedByUserId: string | null;
  certifiedByUserId: string | null;
  certifiedDate: string | null;
  justification: string | null;
  notes: string | null;
}

/** PR 3 §7.2 — checklist row joined with catalog metadata. */
export interface LoanSetupChecklistRowDto extends LoanSetupChecklistItemDto {
  catalogCode: string;
  catalogTitle: string;
  catalogTier: string;
  catalogOwnerRoleCode: string;
  catalogDescription: string | null;
  catalogDisplayOrder: number;
  catalogSection: string | null;
  /** Drives the "Due when … is recorded" copy for deferred due dates
   *  (the server stores a min-value DueDate until the anchor exists). */
  catalogDueDateFormula: string | null;
}

/** Loan record foundations — the eight Setup-tab workflow sections, in
 *  display order. Must match the server's LoanSetupSections.All exactly. */
export const LOAN_SETUP_SECTIONS = [
  "Boarding & payments",
  "Filings & collateral",
  "Insurance",
  "Team & vendors",
  "Schedules & reporting",
  "Governance & transition",
  "Document handoff",
  "Final sign-off",
] as const;
export type LoanSetupSection = (typeof LOAN_SETUP_SECTIONS)[number];

// ----- Setup catalog admin (Loan record foundations) ------------------

export interface LoanSetupCatalogItemDto {
  id: string;
  code: string;
  tier: string;
  ownerRoleCode: string;
  title: string;
  description: string | null;
  section: string | null;
  dueDateFormula: string | null;
  isActive: boolean;
  displayOrder: number;
}

export interface CreateSetupCatalogItemRequest {
  title: string;
  tier: string;
  ownerRoleCode: string;
  section: string;
  dueDateFormula: string;
  description?: string | null;
}

export interface UpdateSetupCatalogItemRequest {
  title: string;
  ownerRoleCode: string;
  section: string;
  dueDateFormula: string;
  description?: string | null;
  /** Null/omitted = leave unchanged. A tier change moves the completion
   *  gate on every loan's checklist, so it requires tierChangeReason. */
  tier?: string | null;
  tierChangeReason?: string | null;
}

export interface CompleteSetupItemRequest {
  attachedDocId?: string | null;
  justification?: string | null;
}

export interface JustificationRequest {
  reason: string;
}

/** Both dates optional — when omitted the server derives them from the
 *  loan + its escrow record (the "Generate now" path). */
export interface GenerateSetupChecklistRequest {
  closeDate?: string | null;
  firstPaymentDate?: string | null;
}

// ============================== 7-pill assignments ====================

/** Spec §6 — 7-pill assignment slot status. */
export type LoanAssignmentStatus = "Unassigned" | "Assigned" | "NotApplicable";

/** Spec §6 — Loan Administration 7-pill role codes. Order matches the
 *  server's LoanAssignmentService.PillRoleCodes exactly. (2026-06-09:
 *  A3's permanent slot was replaced by B1 Loan Administrator.) */
export type LoanRoleCode =
  | "B1"
  | "B3"
  | "B4"
  | "E1-VM"
  | "E2"
  | "E1"
  | "B5";

export const LOAN_ROLE_CODES: readonly LoanRoleCode[] = [
  "B1",
  "B3",
  "B4",
  "E1-VM",
  "E2",
  "E1",
  "B5",
] as const;

export const LOAN_ROLE_LABEL: Record<LoanRoleCode, string> = {
  // Deliberate divergence: the pill displays "Loan Administrator" while
  // RoleCodes.cs keeps the canonical title "Sr Loan Admin / Portfolio
  // Manager" — do not "fix" one to match the other.
  B1: "Loan Administrator",
  B3: "Reporting & Accounting",
  B4: "Collateral, Compliance & Workout",
  "E1-VM": "Vendor Relations Lead",
  E2: "Exec Lead, Underwriting",
  E1: "Exec Lead, Loan Admin",
  B5: "QA & Audit Analyst",
};

/** Setup catalog Owner-role multiselect options — the staff role codes
 *  relevant to loan administration. Labels come from the 7-pill label map
 *  above; B2 isn't a pill role so it carries its RoleCodes.cs title.
 *  (Canonical stored code is "E1-VM" per RoleCodes.cs; some seeded rows
 *  carry legacy codes like "E-VM"/"A3"/"E3" — those hydrate as extra
 *  options so existing values round-trip unchanged.) */
export const SETUP_OWNER_ROLE_CODES = [
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
  "E1-VM",
  "E1",
  "E2",
] as const;

export const SETUP_OWNER_ROLE_LABEL: Record<string, string> = {
  B1: LOAN_ROLE_LABEL.B1,
  B2: "Closing / Transaction Coord",
  B3: LOAN_ROLE_LABEL.B3,
  B4: LOAN_ROLE_LABEL.B4,
  B5: LOAN_ROLE_LABEL.B5,
  "E1-VM": LOAN_ROLE_LABEL["E1-VM"],
  E1: LOAN_ROLE_LABEL.E1,
  E2: LOAN_ROLE_LABEL.E2,
};

export interface LoanAssignmentDto {
  id: string;
  loanId: string;
  roleCode: string;
  status: string;
  assignedUserId: string | null;
  assignedDate: string | null;
  assignedByUserId: string | null;
}

export interface AssignLoanRoleRequest {
  /** "assign" | "unassign" | "not-applicable" */
  action: string;
  assignedUserId?: string | null;
  justification?: string | null;
}

// ============================== parties (§7.3) ========================

/** Spec §7.3 — counterparty surface flavors supported by the Parties tab.
 *  The wire is a free-form string; this union documents the values the
 *  Add Party modal uses today. */
export type LoanPartyCounterpartyType =
  | "Business"
  | "Person"
  | "LegalEntity"
  | "Broker"
  | "Vendor"
  | "LocalEnforcer"
  | "PhoenixUser"
  | "OtherContact";

export interface LoanPartyAssignmentDto {
  id: string;
  loanId: string;
  counterpartyType: string;
  counterpartyId: string;
  roleOnLoan: string;
  isActive: boolean;
  emailOverride: string | null;
  phoneOverride: string | null;
  addressOverride: string | null;
  notes: string | null;
  createdAt: string;
  /** Resolved server-side per counterpartyType; null for OtherContact. */
  counterpartyName: string | null;
  /** EFFECTIVE contact info — loan-scoped override when set, else the
   *  counterparty's canonical value. */
  email: string | null;
  phone: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  isKeyPerson: boolean;
  isPrimaryContact: boolean;
}

export interface AddLoanPartyRequest {
  counterpartyType: string;
  counterpartyId: string;
  roleOnLoan: string;
  notes?: string | null;
  /** ISO date (YYYY-MM-DD). */
  effectiveFrom?: string | null;
  isKeyPerson?: boolean;
  isPrimaryContact?: boolean;
}

// ---- Vendor Connection v1 ("Make a Connection") ----------------------

export type VendorConnectionEngagementStatus =
  | "new"
  | "contacted"
  | "in-negotiation"
  | "under-contract";

export interface VendorConnectionDto {
  id: string;
  partyAssignmentId: string;
  loanId: string;
  vendorId: string;
  vendorName: string;
  roleOnLoan: string;
  connectedAt: string;
  connectedByUserId: string | null;
  disconnectedAt: string | null;
  disconnectReason: string | null;
  endReason: string | null;
  /** Read-time liveness: not disconnected AND the assignment is active
   *  and effective. */
  isLive: boolean;
  engagementStatus: VendorConnectionEngagementStatus;
  contractDocumentId: string | null;
  vendorNotes: string | null;
  buyerNotes: string | null;
}

/** Null/omitted = leave unchanged (FIND-12 discipline). Override strings:
 *  "" clears. Dates are strings so "clear" is expressible: "" clears, ISO
 *  yyyy-MM-dd sets. */
export interface UpdateLoanPartyRequest {
  roleOnLoan?: string | null;
  emailOverride?: string | null;
  phoneOverride?: string | null;
  addressOverride?: string | null;
  notes?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  isKeyPerson?: boolean | null;
  isPrimaryContact?: boolean | null;
}

export interface SeedPartiesFromDealResultDto {
  createdCount: number;
  skippedExistingCount: number;
}

// ----- Loan profile admin fields (Loan record foundations) ------------

export type LoanProfileFieldType = "text" | "number" | "date" | "yesno";

export interface LoanProfileFieldDefinitionDto {
  id: string;
  key: string;
  label: string;
  fieldType: string;
  sortOrder: number;
  isActive: boolean;
}

/** One admin-defined field merged with its per-loan value. */
export interface LoanProfileFieldValueDto {
  definitionId: string;
  key: string;
  label: string;
  fieldType: string;
  sortOrder: number;
  value: string | null;
}

export interface CreateLoanProfileFieldDefinitionRequest {
  key: string;
  label: string;
  fieldType: string;
}

export interface UpdateLoanProfileFieldDefinitionRequest {
  label?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
}

export interface SetLoanProfileFieldValueRequest {
  value: string | null;
}

// ============================== compliance calendar (§7.6) ===========

/** Spec §7.6 — severity tiers driving the color band + escalation rules. */
export type ComplianceSeverity =
  | "Catastrophic"
  | "Compliance-EOD"
  | "Compliance-Cure-Available"
  | "Operational"
  | "Information"
  | "minor";

/** Spec §7.6 — calendar item lifecycle. */
export type ComplianceCalendarStatus =
  | "Open"
  | "In-Progress"
  | "Completed"
  | "Waived"
  | "Overdue";

export interface ComplianceCalendarItemDto {
  id: string;
  loanId: string | null;
  sourceType: string;
  sourceEntityId: string | null;
  sourceEntityType: string | null;
  dueDate: string;
  title: string;
  description: string | null;
  status: string;
  severity: string;
  assignedToUserId: string | null;
  completedDate: string | null;
}

export interface CompleteCalendarItemRequest {
  notes?: string | null;
}

export interface SnoozeCalendarItemRequest {
  /** ISO date (YYYY-MM-DD). */
  newDueDate: string;
  reason: string;
}

// ============================== insurance (§7.4) =======================

/** Spec §7.4 — 5 required policy types per RequiredInsurancePolicyCatalogItem seeds. */
export type InsurancePolicyType =
  | "GeneralLiability"
  | "Property"
  | "BusinessInterruption"
  | "WorkersComp"
  | "CyberLiability"
  | "Other";

export interface InsurancePolicyDto {
  id: string;
  loanId: string;
  policyType: string;
  carrierName: string | null;
  policyNumber: string | null;
  coverageAmount: number | null;
  effectiveDate: string | null;
  expirationDate: string;
  status: string;
  phoenixNamedAsVerifiedDate: string | null;
  sellerNamedAsVerifiedDate: string | null;
}

export interface UpsertInsurancePolicyRequest {
  policyType: string;
  carrierName?: string | null;
  policyNumber?: string | null;
  coverageAmount?: number | null;
  /** ISO date (YYYY-MM-DD). */
  effectiveDate?: string | null;
  /** ISO date (YYYY-MM-DD). */
  expirationDate: string;
}

// ============================== UCC (§7.5) =============================

export type UccFilingType =
  | "UCC-1"
  | "UCC-3-Continuation"
  | "UCC-3-Amendment"
  | "UCC-3-Termination"
  | "UCC-3-Assignment";

export interface UccFilingDto {
  id: string;
  loanId: string;
  filingType: string;
  parentFilingId: string | null;
  filingNumber: string;
  filingDate: string;
  filingOffice: string;
  filingJurisdiction: string;
  lapseDate: string;
  currentStatus: string;
  securedPartyLegalName: string;
  debtorLegalName: string;
  isLongTerm: boolean;
  continuationWindowOpen: string | null;
  continuationWindowClose: string | null;
  /** Set when this row was auto-mirrored from a Baselayer Bgc filing. */
  bgcUccFilingId: string | null;
  /** Compliance variance note (e.g. orphan continuation mirrored without a parent). */
  varianceNote: string | null;
}

export interface FileUcc1Request {
  loanId: string;
  filingNumber: string;
  /** ISO date. */
  filingDate: string;
  filingOffice: string;
  filingJurisdiction: string;
  filingMethod?: string | null;
  filingFeesPaid?: number | null;
  isLongTerm?: boolean;
  securedPartyLegalName: string;
  securedPartyType?: string | null;
  securedPartyMailingLine1?: string | null;
  securedPartyCity?: string | null;
  securedPartyState?: string | null;
  securedPartyZip?: string | null;
  debtorLegalName: string;
  debtorType?: string | null;
  debtorJurisdictionOfOrg?: string | null;
  debtorMailingState?: string | null;
  collateralDescriptionType?: string | null;
  collateralDescriptionText?: string | null;
}

export interface FileUccContinuationRequest {
  parentFilingId: string;
  /** ISO date. */
  filingDate: string;
  filingNumber: string;
}

export interface FileUccTerminationRequest {
  parentFilingId: string;
  /** ISO date. */
  filingDate: string;
  filingNumber: string;
  authorizationBasis: string;
}

export interface FileUccAmendmentRequest {
  parentFilingId: string;
  loanId: string;
  /** ISO date. */
  filingDate: string;
  filingNumber: string;
  filingOffice: string;
  filingJurisdiction: string;
  amendmentBody?: string | null;
}

export interface UccLienSearchDto {
  id: string;
  loanId: string;
  searchPurpose: string;
  searchedDebtorName: string;
  searchedJurisdiction: string;
  searchOrderedDate: string;
  searchAsOfDate: string | null;
  searchResultsCount: number;
  expectedPriorityPosition: number | null;
  observedPriorityPosition: number | null;
  varianceFlag: boolean;
  reviewedByUserId: string | null;
  searchProvider: string | null;
  /** Set when this search was auto-mirrored from a completed Baselayer check. */
  backgroundCheckId: string | null;
}

export interface RecordUccLienSearchRequest {
  loanId: string;
  searchPurpose: string;
  searchedDebtorName: string;
  searchedJurisdiction: string;
  searchProvider?: string | null;
  /** ISO date. */
  searchOrderedDate: string;
  /** ISO date. */
  searchAsOfDate?: string | null;
  searchResultsCount: number;
  priorLiensFoundJson?: string | null;
  expectedPriorityPosition?: number | null;
  observedPriorityPosition?: number | null;
  searchCertificationDocId?: string | null;
}

// ============================== vendors (registry) ====================

export interface VendorDto {
  id: string;
  name: string;
  vendorType: string | null;
  status: string;
  // Vendor record foundations — contact profile (optional so pre-existing
  // fixtures stay valid; the server always sends them).
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  // Vendor Program v2 PR 3 — billing-terms audit affordance (notes + an
  // optional terms document reference).
  billingTermsNotes?: string | null;
  billingTermsDocumentId?: string | null;
  // Archive overlay (PortalUser pattern) — hidden from the default registry
  // + pickers; optional so pre-existing fixtures stay valid.
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  // F11 — regulated (borrower-paid) vendor: Phoenix does not invoice them;
  // the monthly report's §4 captures what they collected from the borrower.
  // Optional so pre-existing fixtures stay valid; the server always sends it.
  isRegulated?: boolean;
  // W7 — registry-list rollups, sent by the LIST endpoint only (the
  // single-vendor reads leave them undefined). These three columns rendered
  // hardcoded placeholders while the data behind them already existed.
  averageRating?: number | null;
  ratingCount?: number | null;
  activeAssignmentCount?: number | null;
  ytdVfi?: number | null;
}

export interface CreateVendorRequest {
  name: string;
  vendorType?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  /** Vendor create dedupe — set true (with an optional reason) to create
   *  anyway after a Likely/Strong duplicate 409; the override is audited. */
  confirmCreateNew?: boolean;
  createNewReason?: string | null;
}

/** Archive / unarchive a vendor — optional audit reason. */
export interface ArchiveVendorRequest {
  reason?: string | null;
}

/** What archiving a vendor touches — shown in the confirm modal. Counts
 *  are informational only; archiving never mutates assignments,
 *  connections, invoices, or the linked portal account. */
export interface VendorArchiveImpactDto {
  activeAssignmentCount: number;
  liveConnectionCount: number;
  openInvoiceCount: number;
  hasPortalAccount: boolean;
  portalAccountIsDisabled: boolean;
  portalAccountIsArchived: boolean;
  portalUserId: string | null;
}

/** Vendor record foundations — profile edit. FIND-12 semantics on the wire:
 *  null = unchanged, "" = clear (the edit drawer hydrates every field and
 *  always sends all of them). Name cannot be cleared. */
export interface UpdateVendorProfileRequest {
  name?: string | null;
  vendorType?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  billingTermsNotes?: string | null;
  billingTermsDocumentId?: string | null;
  /** F11 — regulated (borrower-paid) flag. FIND-12: null/omitted = unchanged. */
  isRegulated?: boolean | null;
}

export interface UpdateVendorStatusRequest {
  toStatus: string;
  reason?: string | null;
}

/** Cross-loan view — one vendor-type LoanPartyAssignment row joined to its
 *  loan. Inactive rows = former assignments. */
export interface VendorLoanAssignmentDto {
  assignmentId: string;
  loanId: string;
  loanNumber: string;
  accountState: string | null;
  roleOnLoan: string;
  isActive: boolean;
  assignedAt: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

/** The vendor's linked portal login (PortalUser.LinkedVendorId); the
 *  endpoint returns null data when no account exists yet. */
export interface VendorPortalAccountDto {
  portalUserId: string;
  email: string;
  isActivated: boolean;
  isDisabled: boolean;
  isArchived: boolean;
  lastLoginUtc: string | null;
}

// ---------- Required-vendor catalog (feeds the "Assign vendor" dropdown) --
// The per-loan vacancy matrix + its 60-day covenant automation were retired.

export interface RequiredVendorCatalogItemDto {
  id: string;
  code: string;
  vendorType: string;
  title: string;
  description: string | null;
  requiredCondition: string | null;
  isOptional: boolean;
  displayOrder: number;
}

// Portfolio Administration v1.0 PR 5 — multi-select filter shape used by
// the Vendors registry list page. The single-value `name` / `type` /
// `status` parameters are preserved on the wire for backwards compat with
// the PR 2a clients.
export interface VendorSearchParams {
  search?: string;
  types?: string[];
  statuses?: string[];
  /** Archived vendors are excluded server-side by default; the registry's
   *  "Show archived" toggle sets this. */
  includeArchived?: boolean;
}

// ---------- Vendor Billing v1.0 — VFI invoices --------------------------

export type VendorInvoiceStatus =
  | "draft"
  | "issued"
  | "viewed"
  | "processing"
  | "paid"
  | "overdue"
  | "void";

/** Default due-date offset (issue + 30 days) applied when the issue modal
 *  leaves the due date blank. Mirrors `VendorInvoiceDefaults.DueDays`. */
export const VENDOR_INVOICE_DEFAULT_DUE_DAYS = 30;

export interface VendorInvoiceDto {
  id: string;
  vendorId: string;
  vendorName: string;
  loanId: string;
  loanNumber: string;
  invoiceNumber: string;
  status: VendorInvoiceStatus;
  amount: number;
  description: string | null;
  issuedAt: string | null;
  dueDate: string | null;
  viewedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  paymentMethod: string | null;
  /** Vendor Program v2 PR 3 — the accepted monthly report this draft was
   *  generated from; null for manually issued invoices. Optional so
   *  pre-existing fixtures stay valid (the server always sends it). */
  sourceReportId?: string | null;
  /** SP-4 WS-3 — who the invoice bills: "vendor" (the original flow) or
   *  "borrower" (a regulated vendor's fees billed through Phoenix, payable
   *  in the buyer portal). Optional so pre-existing fixtures stay valid. */
  audience?: VendorInvoiceAudience;
  /** SP-4 WS-2 — the at-issue contract-check snapshot (audit trail). */
  contractExpectedAtIssue?: number | null;
  contractVarianceAckReason?: string | null;
  /** SP-4 WS-2 — the LIVE contract check, computed server-side for DRAFT
   *  invoices only (null on issued+ rows — those carry the snapshot). */
  contractComparison?: ContractComparisonDto | null;
  /** Step 6 W3 — the provenance behind `contractExpectedAtIssue`, copied at
   *  issue rather than referenced, so it still reads after the parent term is
   *  re-tiered. Tier fields are null for a non-tiered term. */
  contractTermEffectiveFromAtIssue?: string | null;
  contractTierRateTypeAtIssue?: VendorTierRateType | null;
  contractTierRateAtIssue?: number | null;
  contractTierLowerThresholdAtIssue?: number | null;
  contractTierUpperThresholdAtIssue?: number | null;
}

export type VendorInvoiceAudience = "vendor" | "borrower";

// ---- SP-4 WS-1/WS-2 — vendor contract terms + compute-and-compare ------

export type VendorFeeBasis =
  | "FlatPerEngagement"
  | "Hourly"
  | "PercentOfValue"
  /** Step 6 — banded pricing with FLAT LOOKUP: the whole period volume bills
   *  at the single rate of the band it lands in, never marginally. Priced
   *  from `tiers`; `amount` is unused. */
  | "Tiered";

/** Step 6 — how a band prices the volume that lands in it. */
export type VendorTierRateType = "Percent" | "Flat";

/** Step 6 — one band. `upperThreshold` is INCLUSIVE and null marks the
 *  open-ended top band; `rate` is a FRACTION for Percent (0.30 = 30%) and
 *  flat dollars for Flat. */
export interface VendorContractTermTierDto {
  id: string;
  sortOrder: number;
  lowerThreshold: number;
  upperThreshold: number | null;
  rateType: VendorTierRateType;
  rate: number;
}

export interface VendorContractTermDto {
  id: string;
  vendorId: string;
  basis: VendorFeeBasis;
  /** Flat dollars, dollars/hour, or a FRACTION (0.02 = 2%) per basis. */
  amount: number;
  effectiveFrom: string;
  /** Null on the currently-effective row. */
  effectiveTo: string | null;
  notes: string | null;
  createdAt: string;
  createdByUserId: string | null;
  /** Step 6 — ascending bands; null/absent for every basis but Tiered. */
  tiers?: VendorContractTermTierDto[] | null;
}

export interface CreateVendorContractTermTierRequest {
  lowerThreshold: number;
  /** Inclusive; null ONLY on the last band. */
  upperThreshold: number | null;
  rateType: VendorTierRateType;
  /** A FRACTION for Percent (0.30 = 30%); flat dollars for Flat. */
  rate: number;
}

export interface CreateVendorContractTermRequest {
  basis: VendorFeeBasis;
  /** Ignored (and stored as 0) for a Tiered term, which prices from `tiers`. */
  amount: number;
  effectiveFrom: string;
  notes?: string | null;
  /** Step 6 — required (and only permitted) for Tiered, in ascending band
   *  order. The server assigns sort order and validates contiguity. */
  tiers?: CreateVendorContractTermTierRequest[] | null;
}

// ---- Step 6 W5 — terms proposed from a countersigned schedule ---------

export type VendorContractTermProposalStatus =
  | "pending"
  | "confirmed"
  | "declined"
  | "superseded";

/** A reading of a signed compensation schedule, awaiting a human. Nothing
 *  bills differently until one is confirmed. */
export interface VendorContractTermProposalDto {
  id: string;
  vendorId: string;
  status: VendorContractTermProposalStatus;
  basis: VendorFeeBasis;
  /** Null when the schedule left the Engagement Start Date blank — the
   *  confirm form then requires one rather than defaulting silently. */
  proposedEffectiveFrom: string | null;
  notes: string | null;
  tiers: VendorContractTermTierDto[];
  sourceDocumentId: string | null;
  confirmedTermId: string | null;
  confirmedAt: string | null;
  declinedReason: string | null;
  createdAt: string;
}

export interface ConfirmVendorContractTermProposalRequest {
  effectiveFrom: string;
  notes?: string | null;
}

export interface DeclineVendorContractTermProposalRequest {
  reason: string;
}

export type ContractComparisonStatus =
  | "match"
  | "variance"
  | "no-terms"
  | "needs-quantity";

/** "Contract says $450 (3 hrs × $150). Invoice says $520. Difference: $70." */
export interface ContractComparisonDto {
  status: ContractComparisonStatus;
  invoicedAmount: number;
  expectedAmount: number | null;
  delta: number | null;
  basis: VendorFeeBasis | null;
  termSummary: string | null;
  reportedQuantity: number | null;
  // Which terms produced the number, and the date they were resolved as of —
  // optional so pre-existing fixtures stay valid; the server always sends them.
  termId?: string | null;
  termEffectiveFrom?: string | null;
  asOf?: string | null;
  /** Step 6 — WHICH band priced a tiered term, with its values alongside the
   *  id so the answer survives a later re-tiering. Null for other bases. */
  appliedTierId?: string | null;
  appliedTierRateType?: VendorTierRateType | null;
  appliedTierRate?: number | null;
  appliedTierLowerThreshold?: number | null;
  appliedTierUpperThreshold?: number | null;
}

export interface VendorInvoicePaymentDto {
  id: string;
  amount: number;
  receivedAt: string;
  method: string;
  note: string | null;
  recordedByUserId: string | null;
  createdAt: string;
  /** Set when the invoice was corrected out of Paid — the receipt happened and
   *  was then reversed, so the row is annotated rather than deleted. Optional
   *  so pre-existing fixtures stay valid; the server always sends them. */
  reversedAt?: string | null;
  reversalReason?: string | null;
}

/** Stored Stripe linkage + the most recent verified webhook event — what
 *  the drawer's "Online payment" block renders (no live Stripe call). */
export interface VendorInvoiceStripeInfoDto {
  paymentIntentId: string;
  lastEventAt: string | null;
  lastEventType: string | null;
}

export interface VendorInvoiceDetailDto {
  invoice: VendorInvoiceDto;
  payments: VendorInvoicePaymentDto[];
  stripePayment?: VendorInvoiceStripeInfoDto | null;
}

export interface IssueVendorInvoiceRequest {
  vendorId: string;
  loanId: string;
  amount: number;
  description?: string | null;
  /** ISO date; omitted → issue + 30 days (server default). */
  dueDate?: string | null;
  /** Required by the server only when the contract check reports a variance —
   *  the same gate the draft-issue path applies. */
  varianceAckReason?: string | null;
}

/** W6 — the server-computed VFI rollup for ONE vendor or ONE loan.
 *
 *  Replaces two client-side computations that disagreed with the registry
 *  dashboard and with each other: "open" counted `processing` here but not
 *  there, and Paid-YTD was measured from the browser's LOCAL 1 January of the
 *  UTC year. Both now come from the server against one set of definitions.
 *
 *  The headline figures are the VENDOR's. Borrower-billed (regulated-vendor)
 *  invoices are reported separately and labelled — the vendor does not owe
 *  them, but they are real money and must not vanish from the screen. */
export interface VfiScopedSummaryDto {
  openTotal: number;
  openCount: number;
  overdueTotal: number;
  overdueCount: number;
  inTransitTotal: number;
  inTransitCount: number;
  paidYtdTotal: number;
  paidYtdCount: number;
  borrowerBilledOpenTotal: number;
  borrowerBilledOpenCount: number;
}

export interface VendorInvoiceListParams {
  vendorId?: string;
  loanId?: string;
  status?: VendorInvoiceStatus;
  /** W6/D3 — the staff surfaces pass "vendor" so a regulated vendor's
   *  borrower-billed invoices stop inflating that vendor's own figures.
   *  Omitted returns both streams. */
  audience?: VendorInvoiceAudience;
}

export interface VfiOverdueBucketDto {
  label: string;
  count: number;
  amount: number;
}

export interface VfiTopVendorDto {
  vendorId: string;
  vendorName: string;
  outstanding: number;
  openCount: number;
  overdueCount: number;
}

export interface VfiDashboardDto {
  /** Excludes in-transit (processing) amounts — see inTransitTotal. */
  outstandingTotal: number;
  openCount: number;
  paidYtd: number;
  paidYtdCount: number;
  overdueBuckets: VfiOverdueBucketDto[];
  topVendorsByOutstanding: VfiTopVendorDto[];
  /** Stripe payments mid-settlement (ACH processing): money on its way. */
  inTransitTotal: number;
  inTransitCount: number;
}

// ---------- Vendor Program v2 PR 3 — VFI Accounting -----------------------

export interface BatchIssueRequest {
  invoiceIds: string[];
}

export interface BatchIssueFailureDto {
  id: string;
  error: string;
}

/** Batch send is per-item isolated: non-Draft ids fail individually with a
 *  reason, never the whole batch. */
export interface BatchIssueResultDto {
  issued: VendorInvoiceDto[];
  failed: BatchIssueFailureDto[];
}

export interface VfiPastIncomeRowDto {
  /** First day of the collection month (ISO). */
  month: string;
  vendorId: string;
  vendorName: string;
  collected: number;
  invoiceCount: number;
}

/** Shared row shape for the receivables-aging and outstanding reports. */
export interface VfiAgingRowDto {
  id: string;
  invoiceNumber: string;
  vendorName: string;
  loanNumber: string;
  status: VendorInvoiceStatus;
  amount: number;
  issuedAt: string | null;
  dueDate: string | null;
  ageDays: number;
}

export interface VfiRatingRollupDto {
  averageOverall: number | null;
  signOffCount: number;
}
